import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Send } from 'lucide-react';
import Button from '../ui/Button';

interface MessageInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
}

export default function MessageInput({
  onSend,
  isLoading = false,
  placeholder = 'Ask a question about companies, enterprise systems, or business domains...',
  value: controlledValue,
  onChange: controlledOnChange,
}: MessageInputProps) {
  const [internalMessage, setInternalMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Use controlled value if provided, otherwise use internal state
  const message = controlledValue !== undefined ? controlledValue : internalMessage;
  const setMessage = controlledOnChange || setInternalMessage;

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  const handleSubmit = () => {
    if (message.trim() && !isLoading) {
      onSend(message.trim());
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-neutral-200 bg-white p-4">
      <div className="flex items-end gap-3">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isLoading}
            rows={1}
            className="w-full resize-none rounded-lg border border-neutral-300 px-4 py-3 pr-12 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:bg-neutral-100 disabled:cursor-not-allowed max-h-[200px] overflow-y-auto"
            style={{ minHeight: '48px' }}
          />
        </div>

        <Button
          onClick={handleSubmit}
          disabled={!message.trim() || isLoading}
          className="h-12 px-4"
          title="Send message (Enter)"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>

      <p className="mt-2 text-xs text-neutral-500">
        Press Enter to send, Shift + Enter for new line
      </p>
    </div>
  );
}
